import { useState } from 'react'
import { MATERIAL_TYPE, type MaterialRecord, type MaterialType } from '@henlytics/types'
import { DataTable, type Column } from '../components/common/DataTable'
import { EmptyState } from '../components/common/EmptyState'
import { EntryGrid } from '../components/entry/EntryGrid'
import {
  FarmSettingsFields,
  draftFromSettings,
  inputFromDraft,
  type FarmSettingsDraft,
} from '../components/settings/FarmSettingsFields'
import { useFarmSettings, useUpdateFarmSettings } from '../hooks/useFarm'
import { useCreateMaterial, useMaterials } from '../hooks/useInventory'
import { useSessionInfo } from '../hooks/useSessionInfo'

const materialTypeLabel = (type: MaterialType) => type.replace('_', ' ')

const MATERIAL_COLUMNS: ReadonlyArray<Column<MaterialRecord>> = [
  { key: 'name', header: 'Name', render: (m) => m.name },
  { key: 'type', header: 'Type', render: (m) => materialTypeLabel(m.materialType) },
  { key: 'uom', header: 'Unit', render: (m) => m.uom },
  { key: 'active', header: 'Active', render: (m) => (m.isActive ? 'yes' : 'no') },
]

/**
 * Farm settings and feed materials.
 *
 * Only the owner changes settings; the API enforces that and this page mirrors
 * it. Owners and managers can add feed materials, which daily feed entry needs.
 */
export function Settings() {
  const session = useSessionInfo()
  const settings = useFarmSettings()
  const update = useUpdateFarmSettings()
  const materials = useMaterials()
  const createMaterial = useCreateMaterial()

  // Null until the owner edits something; until then the form shows what is saved.
  const [edits, setEdits] = useState<FarmSettingsDraft | null>(null)
  const [materialName, setMaterialName] = useState('')
  const [materialType, setMaterialType] = useState<MaterialType>('finished_feed')
  const [materialUom, setMaterialUom] = useState('')

  const role = session.data?.role
  const isOwner = role === 'owner'
  const canAddMaterials = role === 'owner' || role === 'manager'

  if (settings.isPending) return <p className="muted">Loading settings…</p>
  if (settings.isError) {
    return (
      <p role="alert" className="error">
        {settings.error.message}
      </p>
    )
  }

  const draft = edits ?? draftFromSettings(settings.data)
  const uom = materialUom || settings.data.feedUom

  return (
    <div className="page">
      <header className="page-head">
        <h2>Settings</h2>
        {isOwner ? null : <p className="muted">Only the farm owner can change these.</p>}
      </header>

      <EntryGrid
        title="Farm"
        description="Currency and number format apply everywhere. The estimates feed the loaded cost per egg."
        onSubmit={() => update.mutate(inputFromDraft(draft), { onSuccess: () => setEdits(null) })}
        submitLabel="Save settings"
        busy={update.isPending}
        disabled={!isOwner || edits === null}
        error={update.isError ? update.error.message : null}
        success={update.isSuccess && edits === null ? 'Saved.' : null}
      >
        <FarmSettingsFields
          value={draft}
          onChange={(patch) => setEdits({ ...draft, ...patch })}
          currencyLocked={settings.data.currencyLocked}
          disabled={!isOwner}
        />
      </EntryGrid>

      <section>
        <h3>Feed materials</h3>
        <p className="muted">What you issue to the flock on the Daily entry page. Each keeps its own unit.</p>
        <DataTable
          rows={materials.data ?? []}
          columns={MATERIAL_COLUMNS}
          rowKey={(m) => m.id}
          empty={
            <EmptyState
              title="No feed materials yet."
              hint="Add the feed you buy, e.g. “Layer mash”, so daily feed can be recorded."
            />
          }
        />
      </section>

      {canAddMaterials ? (
        <EntryGrid
          title="Add feed material"
          onSubmit={() =>
            createMaterial.mutate(
              { name: materialName.trim(), materialType, uom: uom.trim() },
              {
                onSuccess: () => {
                  setMaterialName('')
                  setMaterialUom('')
                },
              },
            )
          }
          submitLabel="Add material"
          busy={createMaterial.isPending}
          error={createMaterial.isError ? createMaterial.error.message : null}
          success={createMaterial.isSuccess ? 'Added.' : null}
        >
          <label className="cell wide">
            <span className="cell-label">Name</span>
            <input
              className="cell-input"
              value={materialName}
              onChange={(event) => setMaterialName(event.target.value)}
              placeholder="Layer mash"
              required
              maxLength={120}
            />
          </label>
          <label className="cell">
            <span className="cell-label">Type</span>
            <select
              className="cell-input"
              value={materialType}
              onChange={(event) => setMaterialType(event.target.value as MaterialType)}
            >
              {MATERIAL_TYPE.map((type) => (
                <option key={type} value={type}>
                  {materialTypeLabel(type)}
                </option>
              ))}
            </select>
          </label>
          <label className="cell">
            <span className="cell-label">Unit</span>
            <input
              className="cell-input"
              value={uom}
              onChange={(event) => setMaterialUom(event.target.value)}
              required
              maxLength={16}
            />
            <span className="cell-hint">Defaults to the farm&rsquo;s feed unit.</span>
          </label>
        </EntryGrid>
      ) : null}
    </div>
  )
}
